import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import SEOHead, { softwareSchema } from '@components/marketing/SEOHead';
import CtaBand from '@components/marketing/CtaBand';
import { COURTS, SPORTS, ROLES, CAPABILITIES, TABLE, VOICES } from './content';

gsap.registerPlugin(useGSAP, ScrollTrigger);

const TALLY = [
  { label: 'Certificates issued', value: 1240, suffix: '+' },
  { label: 'States connected', value: 28 },
  { label: 'Sports, each scored on its own rules', value: 8 },
];

const SaaSLandingPage = () => {
  const root = useRef(null);

  useGSAP(() => {
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // One orchestrated moment, on the hero: the page settles the way a board
    // does when a match is called on. Everything below it is left alone -
    // a reveal on every section is the thing that reads as automated.
    if (!still) {
      gsap.timeline({ defaults: { ease: 'power3.out' } })
        .from('.stms-hero__title span', { y: 26, opacity: 0, duration: 0.7, stagger: 0.08 })
        .from('.stms-hero__sub', { y: 14, opacity: 0, duration: 0.55 }, '-=0.4')
        .from('.stms-hero__acts .stms-btn', { y: 12, opacity: 0, duration: 0.45, stagger: 0.07 }, '-=0.3')
        .from('.stms-board', { y: 24, opacity: 0, duration: 0.6 }, '-=0.45')
        .from('.stms-board__list li', { x: 16, opacity: 0, duration: 0.4, stagger: 0.06 }, '-=0.3');
    }

    // Figures count once as they arrive. Counting is worth doing on a number
    // and nowhere else, so nothing else on the page moves on scroll.
    gsap.utils.toArray('.stms-count').forEach((el) => {
      const target = Number(el.dataset.to);
      if (still) { el.textContent = target.toLocaleString('en-IN'); return; }
      // Held paused rather than driven by the trigger directly: attaching the
      // ScrollTrigger to the tween fires onUpdate at progress zero during
      // refresh, which wrote "0" over the real figure before anyone scrolled.
      const counter = { value: 0 };
      const count = gsap.to(counter, {
        value: target,
        duration: 1.3,
        ease: 'power2.out',
        paused: true,
        onUpdate: () => {
          el.textContent = Math.round(counter.value).toLocaleString('en-IN');
        },
      });
      ScrollTrigger.create({
        trigger: el,
        start: 'top 88%',
        once: true,
        onEnter: () => count.play(),
      });
    });
  }, { scope: root });

  return (
    <div ref={root}>
      <SEOHead
        title="Tournament management software for sports associations"
        description="Tournament management software that runs a season end to end: registration, draws, scheduling, live scoring, standings and certificates."
        schema={softwareSchema}
      />

      <section className="stms-hero">
        <div className="stms-hero__art" aria-hidden="true" />
        <div className="stms-wrap stms-hero__inner">
          <div className="stms-hero__say">
            <h1 className="stms-hero__title">
              <span>From the first draw</span>
              <span>to the last certificate.</span>
            </h1>
            <p className="stms-hero__sub">
              SportsTMS runs a season end to end — registration, the draw, the order of
              play, scores as they happen, the standings and the certificates. Built for
              sports associations, corporate leagues and the bodies that oversee them.
            </p>
            <div className="stms-hero__acts">
              <Link className="stms-btn stms-btn--solid stms-btn--lg" to="/demo">Book a demo</Link>
              <Link className="stms-btn stms-btn--ghost stms-btn--lg" to="/platform">See what it covers</Link>
            </div>
          </div>

          <div className="stms-board" aria-label="Order of play">
            <div className="stms-board__top">
              <span className="stms-board__where">Order of play</span>
              <span className="stms-board__when">Four courts, one venue</span>
            </div>
            <ul className="stms-board__list">
              {COURTS.map((c) => (
                <li key={c.unit} className={c.live ? 'is-live' : ''}>
                  <span className="stms-board__unit">
                    {c.live && <i className="stms-live" aria-hidden="true" />}
                    {c.unit}
                  </span>
                  <span className="stms-board__tie">
                    <b>{c.home}</b><em>{c.score}</em><b>{c.away}</b>
                  </span>
                  <span className="stms-board__meta">{c.sport} · {c.note}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="stms-tally">
        <div className="stms-wrap">
          <dl>
            {TALLY.map((t) => (
              <div key={t.label}>
                <dt>{t.label}</dt>
                <dd className="stms-num">
                  <span className="stms-count" data-to={t.value}>
                    {t.value.toLocaleString('en-IN')}
                  </span>
                  {t.suffix && <span>{t.suffix}</span>}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className="stms-sec stms-sec--paper">
        <div className="stms-wrap stms-split">
          <div className="stms-split__say">
            <h2>Every sport keeps its own rules</h2>
            <p>
              Most systems give you one generic scoreboard and leave the rest to a
              spreadsheet. Here each sport is scored the way it is actually played, so the
              app refuses a score the sport cannot produce — and the tables built from
              those results can be trusted.
            </p>
            <Link className="stms-btn stms-btn--line" to="/sports">How each sport is scored</Link>
          </div>
          <div className="stms-split__do">
            <dl className="stms-sports">
              {SPORTS.map((s) => (
                <div key={s.sport}>
                  <dt>{s.sport}</dt>
                  <dd>{s.how}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      <section className="stms-sec">
        <div className="stms-wrap">
          <div className="stms-lede">
            <h2>Four people, four views of the same season</h2>
            <p>Each account shows what that job needs and nothing else, so a whole season can run in parallel without anyone treading on anyone.</p>
          </div>

          <div className="stms-roles">
            <figure className="stms-roles__pic">
              <img src="/images/sportstms/scorer.jpg" alt="An official scoring a badminton match on a phone at courtside" loading="lazy" width="1400" height="1050" />
            </figure>
            <div className="stms-roles__col">
              <ol className="stms-roles__list">
                {ROLES.map((r) => (
                  <li key={r.who}>
                    <h3>{r.who}</h3>
                    <p className="stms-roles__scope">{r.scope}</p>
                    <p>{r.text}</p>
                  </li>
                ))}
              </ol>
              {/* Sits under the list it continues, not under the photograph. */}
              <p className="stms-more">
                <Link className="stms-btn stms-btn--line" to="/who-its-for">What each account can do</Link>
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="stms-sec stms-sec--ink">
        <div className="stms-wrap stms-split stms-split--flip">
          <div className="stms-split__say">
            <h2>The table settles the argument</h2>
            <p>
              Three points a win, one a draw. Group tables build themselves as results come in,
              for every city and every category, and the record boards follow — most runs, most
              wickets, most sixes, top scorers.
            </p>
            <Link className="stms-btn stms-btn--ghost" to="/standings">Standings and records</Link>
          </div>
          <div className="stms-split__do">
            <div className="stms-table">
              <div className="stms-table__top">Group A — Men</div>
              <table>
                <thead>
                  <tr>
                    <th scope="col" className="stms-table__pos">#</th>
                    <th scope="col">Team</th>
                    <th scope="col">P</th><th scope="col">W</th><th scope="col">D</th>
                    <th scope="col">L</th><th scope="col">GF</th><th scope="col">GA</th>
                    <th scope="col">GD</th><th scope="col">Pts</th>
                  </tr>
                </thead>
                <tbody>
                  {TABLE.map((r) => (
                    <tr key={r.pos} className={r.pos <= 2 ? 'is-through' : ''}>
                      <td className="stms-table__pos stms-num">{r.pos}</td>
                      <th scope="row">{r.team}</th>
                      <td className="stms-num">{r.p}</td><td className="stms-num">{r.w}</td>
                      <td className="stms-num">{r.d}</td><td className="stms-num">{r.l}</td>
                      <td className="stms-num">{r.gf}</td><td className="stms-num">{r.ga}</td>
                      <td className="stms-num">{r.gd}</td>
                      <td className="stms-num stms-table__pts">{r.pts}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="stms-table__foot">Top two go through to the semi-finals.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="stms-sec stms-sec--paper">
        <div className="stms-wrap">
          <div className="stms-lede">
            <h2>What the platform covers</h2>
            <p>From the day registration opens to the day the certificates go out.</p>
          </div>
          <dl className="stms-caps">
            {CAPABILITIES.map(([term, def]) => (
              <div key={term}>
                <dt>{term}</dt>
                <dd>{def}</dd>
              </div>
            ))}
          </dl>
          <p className="stms-more">
            <Link className="stms-btn stms-btn--line" to="/platform">The full picture, stage by stage</Link>
          </p>
        </div>
      </section>

      <section className="stms-strip" aria-hidden="true" />

      <section className="stms-sec">
        <div className="stms-wrap">
          <div className="stms-lede"><h2>From the people running seasons</h2></div>
          <div className="stms-voices">
            {VOICES.map((v) => (
              <blockquote key={v.name}>
                <p>{v.quote}</p>
                <footer><b>{v.name}</b><span>{v.role}</span></footer>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      <CtaBand title="Bring us your season" image="/images/sportstms/volley.jpg">
        <p>
          Tell us the sports, the venues and roughly how many people. We set up a working
          tenant with your own data so you can see the real thing, not a slideshow.
        </p>
      </CtaBand>
    </div>
  );
};

export default SaaSLandingPage;
